import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getRequestableAgents,
  submitAccessRequest,
  RequestableAgent,
  ApiError,
} from '../lib/api-client';

export const RequestAccess = () => {
  const [agents, setAgents] = useState<RequestableAgent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getRequestableAgents()
      .then((response) => {
        if (!cancelled) {
          setAgents(response.agents);
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) {
          setLoadError(err.message || 'Failed to load agents available to request');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAgentId || !justification.trim()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitAccessRequest(selectedAgentId, justification.trim());
      setSubmitted(true);
      setSelectedAgentId('');
      setJustification('');
    } catch (err) {
      setSubmitError((err as ApiError).message || 'Failed to submit access request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Request Access</h1>
      <div className="form-card">
        {loadError ? <p className="error">{loadError}</p> : null}
        {submitted ? (
          <div className="form-banner success">
            Your access request has been submitted to the agent owners for approval. You can view
            its status from the <Link to="/">Overview</Link> page.
          </div>
        ) : null}
        {submitError ? <p className="error">{submitError}</p> : null}

        {agents !== null && agents.length === 0 && !loadError ? (
          <p className="muted">
            There aren't any agents you're currently eligible to request access to.
          </p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="request-agent">Agent</label>
              <select
                id="request-agent"
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                disabled={agents === null || submitting}
                required
              >
                <option value="" disabled>
                  {agents === null ? 'Loading agents...' : 'Select an agent...'}
                </option>
                {(agents ?? []).map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.agent_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="request-justification">Justification</label>
              <textarea
                id="request-justification"
                rows={3}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                disabled={agents === null || submitting}
                placeholder="Why do you need access to this agent?"
                required
              />
            </div>

            <button
              type="submit"
              className="primary-button"
              style={{ margin: 0 }}
              disabled={agents === null || agents.length === 0 || submitting}
            >
              {submitting ? 'Submitting...' : 'Submit request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
